import { useEffect, useRef, useState } from "react";
import { useFileUpload } from "hooks/useFileUpload";
import { prescriptionReviewService as api } from "services/prescriptionReviewService";

export function uploadError(files) {
  if (!files.length || files.length > 10) return "Choose between 1 and 10 files.";
  if (files.some(file => !/\.(pdf|docx|png|jpe?g)$/i.test(file.name))) return "Use PDF, DOCX, PNG or JPEG files.";
  if (files.some(file => !file.size || file.size > 10 * 1024 * 1024)) return "Each file must be non-empty and no larger than 10 MB.";
  if (files.reduce((size, file) => size + file.size, 0) > 15 * 1024 * 1024) return "The combined files must be no larger than 15 MB.";
  return "";
}

export default function PrescriptionUpload({ onComplete, onCancel }) {
  const { uploadFile } = useFileUpload();
  const [search, setSearch] = useState("");
  const [residents, setResidents] = useState([]);
  const [resident, setResident] = useState(null);
  const [files, setFiles] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("");
  const [error, setError] = useState("");
  const uploaded = useRef([]);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    let active = true;
    if (search.trim().length < 2) { setResidents([]); setSearching(false); return; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try { const result = await api.residents(search.trim()); if (active) { setResidents(result); setError(""); } }
      catch (err) { if (active) setError(err.message || "Unable to find residents. Please retry."); }
      finally { if (active) setSearching(false); }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [search]);
  useEffect(() => {
    if (!busy) return;
    const preventLoss = event => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", preventLoss);
    return () => window.removeEventListener("beforeunload", preventLoss);
  }, [busy]);
  const submit = async event => {
    event.preventDefault();
    if (inFlight.current) return;
    const invalid = !resident?.workspaceId ? "Select the resident and their AFH before uploading." : uploadError(files);
    if (invalid) { setError(invalid); return; }
    inFlight.current = true; setBusy(true); setError("");
    try {
      for (let index = 0; index < files.length; index++) {
        if (uploaded.current[index]) continue;
        const file = files[index];
        setStage(`Uploading file ${index + 1} of ${files.length}...`);
        const result = await uploadFile(null, file, { purpose: "OTHER", fileType: "DOCUMENT", preserveOriginal: true,
          onUploadProgress: percent => { if (mounted.current) setStage(`Uploading file ${index + 1} of ${files.length}: ${percent}%`); } });
        if (!result?.fileId) throw new Error("The document was not stored. Please retry.");
        uploaded.current[index] = { fileId: result.fileId, fileName: file.name, contentType: file.type, size: file.size };
      }
      setStage("Documents stored. Extracting medications and checking the resident. This may take a few minutes...");
      const records = await api.import(resident, files, uploaded.current);
      if (!Array.isArray(records) || !records.length) throw new Error("No medication orders were found. Check the documents and retry.");
      if (mounted.current) onComplete(records);
    } catch (err) { if (mounted.current) setError(err?.response?.data?.message || err.message || "Unable to process the prescription. Please retry."); }
    finally { inFlight.current = false; if (mounted.current) { setBusy(false); setStage(""); } }
  };
  return <section className="pr-panel p-4" aria-label="Upload prescription">
    <h3>Upload prescription documents</h3>
    <p>Upload external prescriptions or onboard medications on behalf of an AFH. The documents will be reviewed here before forwarding to OCare.</p>
    <form onSubmit={submit}>
      <fieldset disabled={busy} className="border-0 p-0">
        <label className="d-block mb-3">Find resident<input className="form-control" value={search} onChange={event => { setSearch(event.target.value); setResident(null); }} placeholder="Search by resident name" /></label>
        {searching ? <p role="status">Finding residents...</p> : null}
        {!searching && search.trim().length >= 2 && !residents.length ? <p>No matching residents. Check the name and try again.</p> : null}
        {residents.length ? <label className="d-block mb-3">Resident and AFH<select className="form-select" value={resident?.id || ""} onChange={event => setResident(residents.find(item => item.id === event.target.value) || null)}><option value="">Select resident</option>{residents.map(item => <option key={item.id} value={item.id}>{item.name} · DOB: {item.dateOfBirth || "Not recorded"} · {item.facility || "AFH not recorded"}</option>)}</select></label> : null}
        {resident ? <p><strong>{resident.name}</strong> · {resident.facility || "AFH not recorded"} · DOB: {resident.dateOfBirth || "Not recorded"}</p> : null}
        <label className="d-block mb-3">Prescription files<input className="form-control" type="file" multiple accept=".pdf,.docx,.png,.jpg,.jpeg" onChange={event => { const next = Array.from(event.target.files || []); setFiles(next); uploaded.current = []; setError(next.length ? uploadError(next) : ""); }} /></label>
        <p className="text-muted">Up to 10 files and 10 pages combined. Maximum 10 MB per file and 15 MB total. Supported: PDF, DOCX, PNG and JPEG.</p>
        {files.length ? <ul>{files.map((file, index) => <li key={`${file.name}-${index}`}>{file.name}</li>)}</ul> : null}
      </fieldset>
      {busy ? <p role="status" aria-live="polite">{stage}</p> : null}
      {error ? <div className="alert alert-danger" role="alert">{error} Your successfully uploaded files are retained for retry while this form remains open.</div> : null}
      <div className="d-flex gap-2"><button className="btn btn-primary" type="submit" disabled={busy || !resident || !files.length}>{busy ? "Processing..." : error ? "Retry extraction" : "Upload and extract"}</button><button className="btn btn-outline-secondary" type="button" disabled={busy} onClick={onCancel}>Cancel</button></div>
    </form>
  </section>;
}
